import { useEffect, useRef, useState } from 'react'

import SecondStepApi from 'lib/api/secondStepApi'
import { adaptContextV4ToLegacyContext } from 'lib/api/adapters/context'
import { isPreviewMode } from 'utils/highschoolHelpers'
import {
  K5_CORE_TRAINING_COURSE_CODE,
  MS_CORE_TRAINING_COURSE_CODE,
} from 'utils/program/training/constants'
import {
  ACCESS_CODES,
  getUserProgramPreferencesFromArray,
  isPreferenceValueSet,
  PREFERENCE_NAMES,
} from 'utils/sitePreferenceHelpers'

const DEFAULT_YEARS_TEACHING = '0-1 year'
const INSTRUCTOR_TRACK = 'Instructor'
const K8_TRAINING_LANDING_PATHS = [
  '/product/elementary/trainings',
  '/product/middle-school/trainings',
]
const refreshCurrentPage = () => window.location.reload()

export const K8_TRAINING_SETUP_LOADING_TEXT = 'Loading program training...'

export const K8_TRAINING_PROGRAMS = [
  {
    accessCode: ACCESS_CODES.K5,
    courseCode: K5_CORE_TRAINING_COURSE_CODE,
    enabledPreferenceName: PREFERENCE_NAMES.K5P_ENABLED,
    instructorDataKey: 'K5SelDigital',
    preferenceKey: 'k5Digital',
  },
  {
    accessCode: ACCESS_CODES.MSP,
    courseCode: MS_CORE_TRAINING_COURSE_CODE,
    enabledPreferenceName: PREFERENCE_NAMES.MSP_ENABLED,
    instructorDataKey: 'MspDigital',
    preferenceKey: 'mspDigital',
  },
]

const hasAdminClaim = claims =>
  claims?.some(claim => ['admin', 'leader'].includes(claim?.toLowerCase()))

const hasProgramAccess = (site, program) =>
  site?.preferences?.some(
    preference =>
      preference?.name?.toLowerCase() === program.enabledPreferenceName &&
      (preference?.value === true ||
        preference?.value === 'true' ||
        preference?.value === 1),
  )

const hasCompletePreferences = preferences =>
  isPreferenceValueSet(preferences?.teachesLessons) &&
  isPreferenceValueSet(preferences?.programTrack) &&
  isPreferenceValueSet(preferences?.yearsTeaching)

export const getK8TrainingAutoSetup = ({ instructorData, site }) => {
  const siteId = site?.siteId || site?.id
  const claims = site?.claims || []

  if (!siteId || !hasAdminClaim(claims)) {
    return null
  }

  const licensedPrograms = K8_TRAINING_PROGRAMS.filter(program =>
    hasProgramAccess(site, program),
  )
  const missingPrograms = licensedPrograms
    .map(program => ({
      program,
      preferences: getUserProgramPreferencesFromArray(site.preferences, {
        accessCode: program.accessCode,
      }),
    }))
    .filter(({ preferences }) => !hasCompletePreferences(preferences))

  const preferencePayload = missingPrograms.reduce(
    (payload, { preferences, program }) => {
      payload[program.preferenceKey] = {
        userProgramPreferences: {
          programTrack: isPreferenceValueSet(preferences.programTrack)
            ? preferences.programTrack
            : INSTRUCTOR_TRACK,
          teachesLessons: isPreferenceValueSet(preferences.teachesLessons)
            ? preferences.teachesLessons
            : true,
          yearsTeaching: isPreferenceValueSet(preferences.yearsTeaching)
            ? preferences.yearsTeaching
            : DEFAULT_YEARS_TEACHING,
        },
      }
      return payload
    },
    {},
  )

  const registrationRequests = licensedPrograms
    .filter(
      program => !instructorData?.[program.instructorDataKey]?.educator?.length,
    )
    .map(program => ({
      contentGroupCode: program.accessCode,
      courseCode: program.courseCode,
      siteId,
    }))

  if (!missingPrograms.length && !registrationRequests.length) {
    return null
  }

  return {
    preferencePayload,
    registrationRequests,
    siteId,
  }
}

export const isK8TrainingLandingPath = pathname =>
  K8_TRAINING_LANDING_PATHS.includes(pathname?.replace(/\/$/, ''))

export const useK8TrainingAutoSetup = ({
  createCourseInstanceByCourse,
  currentSite,
  fetchUserContext,
  fetchUserSites,
  k8InstructorData,
  pathname,
  updateSiteUserPreferences,
}) => {
  const attemptedSetupSites = useRef(new Set())
  const [isSetupRunning, setIsSetupRunning] = useState(false)
  const isEligibleRoute = !isPreviewMode() && isK8TrainingLandingPath(pathname)
  const pendingSetup =
    isEligibleRoute && currentSite
      ? getK8TrainingAutoSetup({
          instructorData: k8InstructorData,
          site: currentSite,
        })
      : null
  const pendingSiteKey = String(pendingSetup?.siteId || '')
  const shouldStartSetup =
    !!pendingSetup && !attemptedSetupSites.current.has(pendingSiteKey)

  useEffect(() => {
    if (!isEligibleRoute || !currentSite) {
      return
    }

    const setup = getK8TrainingAutoSetup({
      instructorData: k8InstructorData,
      site: currentSite,
    })
    const siteKey = String(setup?.siteId || '')

    if (!setup || attemptedSetupSites.current.has(siteKey)) {
      return
    }

    attemptedSetupSites.current.add(siteKey)
    setIsSetupRunning(true)

    const runSetup = async () => {
      const didUpdatePreferences =
        Object.keys(setup.preferencePayload).length > 0

      if (didUpdatePreferences) {
        await updateSiteUserPreferences(setup.siteId, setup.preferencePayload)
      }

      for (const registrationRequest of setup.registrationRequests) {
        await createCourseInstanceByCourse(registrationRequest)
      }

      await fetchUserSites()
      const contextBase =
        SecondStepApi.baseApiAddressForContext || SecondStepApi.baseApiAddress
      const contextPath = contextBase.endsWith('/v4/')
        ? 'context/me'
        : 'context'
      await fetchUserContext(contextBase, {
        contextPath,
        adaptResponse:
          contextPath === 'context/me'
            ? adaptContextV4ToLegacyContext
            : undefined,
      })

      if (didUpdatePreferences) {
        refreshCurrentPage()
      }
    }

    runSetup()
      .catch(() => undefined)
      .finally(() => setIsSetupRunning(false))
  }, [
    createCourseInstanceByCourse,
    currentSite,
    fetchUserContext,
    fetchUserSites,
    isEligibleRoute,
    k8InstructorData,
    pathname,
    updateSiteUserPreferences,
  ])

  return (isEligibleRoute && !currentSite) || isSetupRunning || shouldStartSetup
}
